import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";

function MfaCard() {
  const { user, refreshMe, appSettings } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");

  const start = async () => setSetup((await api.post("/auth/mfa/setup")).data);
  const enable = async () => {
    try {
      await api.post("/auth/mfa/enable", { code });
      toast.success("Two-step verification is on");
      setSetup(null); setCode("");
      refreshMe();
    } catch (e) { toast.error(formatError(e)); }
  };
  const disable = async () => {
    try {
      await api.post("/auth/mfa/disable", { code });
      toast.success("Two-step verification turned off");
      setCode("");
      refreshMe();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="mfa-card">
      <div className="flex items-center gap-2 mb-1">
        {user.mfa_enabled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldOff className="h-5 w-5 text-slate-400" />}
        <h3 className="text-lg font-semibold text-slate-800">Two-step verification (authenticator app)</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4" data-testid="mfa-status">
        {user.mfa_enabled ? "On — you'll be asked for a code each time you sign in." : appSettings?.mfa_required ? "Required by your administrator. Set it up to continue." : "Optional for now — it will become mandatory before go-live."}
      </p>
      {!user.mfa_enabled && !setup && <Button onClick={start} data-testid="mfa-setup-button">Set up authenticator</Button>}
      {setup && (
        <div className="grid sm:grid-cols-[180px_1fr] gap-5 items-start">
          <img src={setup.qr} alt="MFA QR code" className="w-44 h-44 border border-slate-200 rounded-md" data-testid="mfa-qr" />
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Scan with Microsoft Authenticator, Google Authenticator or similar. Or enter this key:</p>
            <code className="block font-mono text-xs bg-slate-50 border border-slate-200 rounded p-2 break-all" data-testid="mfa-secret">{setup.secret}</code>
            <div className="flex gap-2">
              <Input placeholder="6-digit code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="font-mono w-40" data-testid="mfa-enable-code-input" />
              <Button onClick={enable} disabled={code.length !== 6} data-testid="mfa-enable-button">Verify & turn on</Button>
            </div>
          </div>
        </div>
      )}
      {user.mfa_enabled && !appSettings?.mfa_required && (
        <div className="flex gap-2">
          <Input placeholder="Current code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="font-mono w-40" data-testid="mfa-disable-code-input" />
          <Button variant="outline" onClick={disable} disabled={code.length !== 6} data-testid="mfa-disable-button">Turn off</Button>
        </div>
      )}
    </section>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", form);
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "" });
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-3" data-testid="password-card">
      <h3 className="text-lg font-semibold text-slate-800">Change password</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password-input" /></div>
        <div className="space-y-1.5"><Label>New password (min 10)</Label><Input type="password" minLength={10} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" /></div>
      </div>
      <Button type="submit" variant="outline" data-testid="change-password-button">Update password</Button>
    </form>
  );
}

export default function Account() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader title="My account" subtitle={`${user.full_name} · ${user.email} · ${ROLE_LABELS[user.role]}`} testId="account-header" />
      {params.get("enrol") && <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2" data-testid="mfa-enrol-required">Two-step verification is required. Please set it up below.</p>}
      <MfaCard />
      <PasswordCard />
    </div>
  );
}
